import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { ServiceService } from '../../service.service';
import Swal from 'sweetalert2';

interface Question {
  question_id: string;
  question_text: string;
  question_type: 'star_rating' | 'text' | 'multiple_choice' | 'checkbox' | 'yes_no';
  options?: string[];
  is_required: boolean;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
}

interface SurveyData {
  survey_title: string;
  survey_description: string;
  created_by: string;
  questionnaire_sets: QuestionnaireSet[];
}

@Component({
  selector: 'app-question-creation',
  templateUrl: './question-creation.component.html',
  styleUrls: ['./question-creation.component.css']
})
export class QuestionCreationComponent implements OnInit {

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  surveyForm!: FormGroup;
  isLoading = false;
  isSubmitting = false;
  userId: string = '';
  showPreview = false;

  questionTypes = [
    { value: 'star_rating', label: 'Star Rating', icon: '⭐' },
    { value: 'text', label: 'Text Input', icon: '📝' },
    { value: 'multiple_choice', label: 'Multiple Choice', icon: '🔘' },
    { value: 'checkbox', label: 'Checkbox', icon: '☑️' },
    { value: 'yes_no', label: 'Yes/No', icon: '✓✗' }
  ];

  constructor(
    private fb: FormBuilder,
    private service: ServiceService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadUserId();
  }

  initializeForm(): void {
    this.surveyForm = this.fb.group({
      survey_title: ['', [Validators.required, Validators.minLength(3)]],
      survey_description: ['', [Validators.required, Validators.minLength(10)]],
      questionnaire_sets: this.fb.array([])
    });

    // Add initial questionnaire set
    this.addQuestionnaireSet();
  }

  loadUserId(): void {
    // Get user ID from localStorage or session
    this.userId = localStorage.getItem('employee_id') || 
                  localStorage.getItem('admin_id') ||
                  localStorage.getItem('user_id') || 
                  sessionStorage.getItem('user_id') || 
                  '625cc353-c0f4-11ee-86a4-0050568dbb0c'; // Default fallback
  }

  // Getters for form arrays
  get questionnaireSets(): FormArray {
    return this.surveyForm.get('questionnaire_sets') as FormArray;
  }

  getQuestions(setIndex: number): FormArray {
    return this.questionnaireSets.at(setIndex).get('questions') as FormArray;
  }

  getOptions(setIndex: number, questionIndex: number): FormArray {
    return this.getQuestions(setIndex).at(questionIndex).get('options') as FormArray;
  }

  // Create form groups
  createQuestionnaireSet(): FormGroup {
    return this.fb.group({
      set_id: [this.generateSetId()],
      set_name: ['', Validators.required],
      questions: this.fb.array([])
    });
  }

  createQuestion(): FormGroup {
    return this.fb.group({
      question_id: [this.generateQuestionId()],
      question_text: ['', [Validators.required, Validators.minLength(5)]],
      question_type: ['star_rating', Validators.required],
      options: this.fb.array([]),
      is_required: [false]
    });
  }

  createOption(value: string = ''): AbstractControl {
    return this.fb.control(value, [Validators.required, Validators.minLength(1)]);
  }

  // Add methods
  addQuestionnaireSet(): void {
    const setGroup = this.createQuestionnaireSet();
    this.questionnaireSets.push(setGroup);
    const newSetIndex = this.questionnaireSets.length - 1;
    this.addQuestion(newSetIndex);
  }

  addQuestion(setIndex: number): void {
    const questionGroup = this.createQuestion();
    this.getQuestions(setIndex).push(questionGroup);
  }

  addOption(setIndex: number, questionIndex: number): void {
    this.getOptions(setIndex, questionIndex).push(this.createOption());
  }

  // Remove methods
  removeQuestionnaireSet(setIndex: number): void {
    if (this.questionnaireSets.length > 1) {
      this.questionnaireSets.removeAt(setIndex);
      this.showSuccess('Section removed successfully');
    } else {
      this.showWarning('At least one section is required');
    }
  }

  removeQuestion(setIndex: number, questionIndex: number): void {
    const questions = this.getQuestions(setIndex);
    if (questions.length > 1) {
      questions.removeAt(questionIndex);
      this.showInfo('Question removed');
    } else {
      this.showWarning('At least one question is required per section');
    }
  }

  removeOption(setIndex: number, questionIndex: number, optionIndex: number): void {
    const options = this.getOptions(setIndex, questionIndex);
    if (options.length > 2) {
      options.removeAt(optionIndex);
    } else {
      this.showWarning('At least two options are required');
    }
  }

  // Question type change handler
  onQuestionTypeChange(setIndex: number, questionIndex: number): void {
    const question = this.getQuestions(setIndex).at(questionIndex);
    const questionType = question.get('question_type')?.value;
    const optionsArray = this.getOptions(setIndex, questionIndex);

    // Clear existing options
    while (optionsArray.length) {
      optionsArray.removeAt(0);
    }

    // Add default options for types that need them
    if (questionType === 'multiple_choice' || questionType === 'checkbox') {
      this.addOption(setIndex, questionIndex);
      this.addOption(setIndex, questionIndex);
    } else if (questionType === 'yes_no') {
      optionsArray.push(this.createOption('Yes'));
      optionsArray.push(this.createOption('No'));
    }
  }

  // Check if question type needs options
  needsOptions(setIndex: number, questionIndex: number): boolean {
    const questionType = this.getQuestions(setIndex)
      .at(questionIndex)
      .get('question_type')?.value;
    return questionType === 'multiple_choice' || questionType === 'checkbox';
  }

  // ID generators
  generateSetId(): string {
    const count = this.questionnaireSets.length + 1;
    return `SET${count.toString().padStart(3, '0')}`;
  }

  generateQuestionId(): string {
    let totalQuestions = 0;
    for (let i = 0; i < this.questionnaireSets.length; i++) {
      totalQuestions += this.getQuestions(i).length;
    }
    return `Q${(totalQuestions + 1).toString().padStart(3, '0')}`;
  }

  // Form submission
  onSubmit(): void {
    if (this.surveyForm.invalid) {
      this.markFormGroupTouched(this.surveyForm);
      this.showError('Please fill all required fields correctly');
      return;
    }

    this.isSubmitting = true;
    const surveyData = this.prepareSurveyData();

    console.log('Survey Data:', JSON.stringify(surveyData, null, 2));

    this.submitSurvey(surveyData);
  }

  prepareSurveyData(): SurveyData {
    const formValue = this.surveyForm.value;

    return {
      survey_title: formValue.survey_title,
      survey_description: formValue.survey_description,
      created_by: this.userId,
      questionnaire_sets: formValue.questionnaire_sets.map((set: any) => ({
        set_id: set.set_id,
        set_name: set.set_name,
        questions: set.questions.map((q: any) => {
          const question: Question = {
            question_id: q.question_id,
            question_text: q.question_text,
            question_type: q.question_type,
            is_required: q.is_required
          };

          // Add options only for relevant question types
          if (q.question_type === 'multiple_choice' || 
              q.question_type === 'checkbox' || 
              q.question_type === 'yes_no') {
            question.options = q.options.filter((opt: string) => opt && opt.trim() !== '');
          }

          return question;
        })
      }))
    };
  }

  submitSurvey(surveyData: SurveyData): void {
    this.service.createCustomerSurveyQuestionnaire(surveyData).subscribe({
      next: (response: any) => {
        console.log('Survey created successfully:', response);
        this.showSuccess('Survey created successfully!');
        this.isSubmitting = false;
        
        // Reset form after successful submission
        setTimeout(() => {
          this.resetForm();
        }, 1500);
      },
      error: (error) => {
        console.error('Error creating survey:', error);
        this.showError(error.error?.message || 'Failed to create survey. Please try again.');
        this.isSubmitting = false;
      }
    });
  }

  // Utility methods
  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  resetForm(): void {
    this.surveyForm.reset();
    while (this.questionnaireSets.length) {
      this.questionnaireSets.removeAt(0);
    }
    this.addQuestionnaireSet();
    this.showInfo('Form reset successfully');
  }

  togglePreview(): void {
    this.showPreview = !this.showPreview;
  }

  duplicateQuestion(setIndex: number, questionIndex: number): void {
    const questionToDuplicate = this.getQuestions(setIndex).at(questionIndex).value;
    const newQuestion = this.createQuestion();
    
    newQuestion.patchValue({
      ...questionToDuplicate,
      question_id: this.generateQuestionId()
    });

    // Copy options if they exist
    if (questionToDuplicate.options && questionToDuplicate.options.length > 0) {
      const optionsArray = newQuestion.get('options') as FormArray;
      questionToDuplicate.options.forEach((opt: string) => {
        optionsArray.push(this.createOption(opt));
      });
    }

    this.getQuestions(setIndex).insert(questionIndex + 1, newQuestion);
    this.showSuccess('Question duplicated');
  }

  moveQuestionUp(setIndex: number, questionIndex: number): void {
    if (questionIndex > 0) {
      const questions = this.getQuestions(setIndex);
      const question = questions.at(questionIndex);
      questions.removeAt(questionIndex);
      questions.insert(questionIndex - 1, question);
    }
  }

  moveQuestionDown(setIndex: number, questionIndex: number): void {
    const questions = this.getQuestions(setIndex);
    if (questionIndex < questions.length - 1) {
      const question = questions.at(questionIndex);
      questions.removeAt(questionIndex);
      questions.insert(questionIndex + 1, question);
    }
  }

  // Toast notification methods
  private showSuccess(message: string): void {
    this.Toast.fire({
      icon: 'success',
      title: message
    });
  }

  private showError(message: string): void {
    this.Toast.fire({
      icon: 'error',
      title: message
    });
  }

  private showWarning(message: string): void {
    this.Toast.fire({
      icon: 'warning',
      title: message
    });
  }

  private showInfo(message: string): void {
    this.Toast.fire({
      icon: 'info',
      title: message
    });
  }

  // Validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.surveyForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getQuestionControl(setIndex: number, questionIndex: number, controlName: string): AbstractControl | null {
    return this.getQuestions(setIndex).at(questionIndex).get(controlName);
  }

  isQuestionFieldInvalid(setIndex: number, questionIndex: number, fieldName: string): boolean {
    const field = this.getQuestionControl(setIndex, questionIndex, fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }
}